// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { forkJoin, Subscription } from "rxjs";

// Custom packages
import { ExportRasDocsDialogComponent } from "src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import {
  documentArchiveExportLoadErrorKey,
  documentArchiveRasAddress,
  documentArchiveRasPdfItems,
} from "src/app/modules/properties/document-archive-export.utils";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { isArchiveObjectId } from "src/app/modules/search-docs/document-archive-general-search.utils";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ApiResponse } from "src/app/shared/interfaces/_base/api-response.interface";
import { DocumentArchiveRasResult } from "src/app/shared/interfaces/document-archive-export.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * What the dossier hands the two export dialogs of the 3.0.0 archive.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveExportDialogData {
  /** The property of the dossier */
  propertyId: string;
}

/**
 * «Esporta RAS» of the dossier on the 3.0.0 archive (FE-M8).
 *
 * The legacy dialog with its data source swapped: it EXTENDS `ExportRasDocsDialogComponent` and only
 * replaces how the lines are read, so the PDF is written by the very `onExportFile` of the legacy, cover,
 * tables, marks and all. The legacy dialog stays as it is for the legacy dossier until the switch.
 *
 * What changes is where the lines come from:
 *  - `GET /document-archive-docs/ras` (NEW-M7) instead of `properties/:id/ras-docs`;
 *  - no `document-archives/settings` and no section computed from the retired `key`: the QR code of a
 *    document is `/qrcode/d/{_id of the row}` (NEW-A3), @see documentArchiveRasPdfItems;
 *  - a failure says so, instead of the legacy "nothing to export".
 *
 * The cover prints `data.companyName`, which the legacy dossier never passes either: same output.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-archive-export-ras-dialog",
  templateUrl: "./document-archive-export-ras-dialog.component.html",
})
export class DocumentArchiveExportRasDialogComponent
  extends ExportRasDocsDialogComponent
  implements OnInit, OnDestroy
{
  /** Translation key of the message shown when the lines cannot be read, `""` when they could */
  loadErrorKey = "";

  /** The two reads of the dialog, cancelled when it closes */
  private loadSubscription: Subscription;
  /** The services the legacy dialog keeps private, under names of their own */
  private readonly cookies: CookieService;
  private readonly properties: PropertiesService;

  /**
   * Class constructor: the dependencies of the legacy dialog, and the service of the new archive
   */
  constructor(
    dialogRef: MatDialogRef<ExportRasDocsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: DocumentArchiveExportDialogData,
    configService: ConfigService,
    cookieService: CookieService,
    translateService: TranslateService,
    propertiesService: PropertiesService,
    helperService: HelperService,
    settingsService: SettingsService,
    private documentArchiveDocsService: DocumentArchiveDocsService
  ) {
    super(
      dialogRef,
      data,
      configService,
      cookieService,
      translateService,
      propertiesService,
      helperService,
      settingsService
    );
    this.cookies = cookieService;
    this.properties = propertiesService;
  }

  /**
   * Read the property, for the cover, and the lines of the RAS, together. Replaces the legacy
   * `ngOnInit` entirely: none of its calls is made.
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.lang = this.cookies.get("lang") || this.configService.defaultLang;

    const propertyId = this.data ? this.data.propertyId : undefined;
    if (!isArchiveObjectId(propertyId)) {
      this.dialogIsLoading = false;
      this.loadErrorKey = documentArchiveExportLoadErrorKey(null);
      return;
    }

    this.loadSubscription = forkJoin([
      this.properties.get(propertyId),
      this.documentArchiveDocsService.rasDocs(propertyId),
    ]).subscribe(
      (results: [any, ApiResponse<DocumentArchiveRasResult>]) => {
        this.dialogIsLoading = false;
        const propertyRes = results[0];
        // ApiService emits a network failure of the legacy route as a VALUE
        if (propertyRes instanceof Error) {
          this.loadErrorKey = documentArchiveExportLoadErrorKey(propertyRes);
          return;
        }
        const property = propertyRes && propertyRes.status && propertyRes.data ? propertyRes.data : null;
        const ras = results[1] && results[1].data ? results[1].data : null;
        if (!property || !ras || !ras.status) {
          this.loadErrorKey = documentArchiveExportLoadErrorKey(null);
          return;
        }

        this.property.setData(property);
        this.propertyData = property;
        this.propertyName = typeof property.name === "string" ? property.name : "";
        this.propertyAddress = documentArchiveRasAddress(property.address);

        const composed = documentArchiveRasPdfItems(ras.rasDocs, this.configService.frontEndHost);
        this.documentArchive = composed.items;
        this.totalPages = composed.totalPages;
      },
      (err: unknown) => {
        this.dialogIsLoading = false;
        this.loadErrorKey = documentArchiveExportLoadErrorKey(err);
      }
    );
  }

  /**
   * Destroy component
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe();
    }
  }
}

/**
 * Open «Esporta RAS» of the 3.0.0 archive for a property, at the size and with the closing rule of the
 * legacy dialog: how the legacy pages that keep the export open it once the switch is live.
 *
 * @since 3.0.0
 *
 * @param dialog     The MatDialog of the page
 * @param propertyId The property
 */
export function openDocumentArchiveExportRasDialog(
  dialog: MatDialog,
  propertyId: string
): MatDialogRef<DocumentArchiveExportRasDialogComponent> {
  const data: DocumentArchiveExportDialogData = { propertyId };
  return dialog.open(DocumentArchiveExportRasDialogComponent, {
    width: "700px",
    data,
    disableClose: true,
  });
}
